import { UserInformation } from "@/api/signin";
import { ENV } from "@/axios/constants";
import {
	KASHU_CONTACT_SUPPORT_URL,
	KASHU_TERMS_OF_SERVICE_URL,
	MIN_TRANSFER,
	PAYOVA_CONTACT_SUPPORT_URL,
	PAYOVA_TERMS_OF_SERVICE_URL,
} from "@/constants/common-constants";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const currencyFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export function isUserFullyVerified(user: UserInformation) {
	return (
		user.location_registration?.missing_steps.includes(
			"IDENTITY_VERIFICATION",
		) === false
	);
}
export const passwordRequirements = [
	{
		id: 1,
		label: "At least 8 characters",
		isValid: (value: string) => value.length >= 8,
	},
	{
		id: 2,
		label: "At least one uppercase letter",
		isValid: (value: string) => /[A-Z]/.test(value),
	},
	{
		id: 3,
		label: "At least one lowercase letter",
		isValid: (value: string) => /[a-z]/.test(value),
	},
	{
		id: 4,
		label: "At least one number",
		isValid: (value: string) => /[0-9]/.test(value),
	},
	{
		id: 5,
		label: "At least one special character",
		isValid: (value: string) => /[^A-Za-z0-9]/.test(value),
	},
] as const;

export const isPasswordValid = (value: string) =>
	passwordRequirements.every((rule) => rule.isValid(value));

// Mobile device detection utility
export const isMobileDevice = (): boolean => {
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
		navigator.userAgent,
	);
};

export const isKashuApp = (): boolean => {
	// use this variable to trigger Kashu layout
	// if not set, the locaitons api will fetch the location settings like  colors / logo / etc
	// Since kashu was never setup to work with v2 mobile settings we need to reach for local settings.
	if (ENV.VITE_DISPLAY_KASHU_ON_DEV === "true") {
		return true;
	}

	return window.location.hostname.includes("kashu");
};

type Hostname = "kashu" | "payova" | "lempay";

export function getHost(): Hostname {
	if (isKashuApp()) {
		return "kashu";
	}
	const isPayova = window.location.hostname.includes("payova");
	if (isPayova) {
		return "payova";
	}
	const isLempay = window.location.hostname.includes("lempay");
	if (isLempay) {
		return "lempay";
	}
	return "kashu";
}

/** Returns the help center / contact support URL for the current host (kashu or payova). */
export function getHelpCenterUrl(): string {
	return getHost() === "payova"
		? PAYOVA_CONTACT_SUPPORT_URL
		: KASHU_CONTACT_SUPPORT_URL;
}

/** Returns the terms of service URL for the current host (kashu or payova). */
export function getTermsOfServiceUrl(): string {
	return getHost() === "payova"
		? PAYOVA_TERMS_OF_SERVICE_URL
		: KASHU_TERMS_OF_SERVICE_URL;
}

export function meetsMinimumTransfer(val: string): boolean {
	return Number(val) >= MIN_TRANSFER;
}

export function meetsMaximumTransfer(val: string, max: number): boolean {
	return Number(val) <= max;
}

export function isValidAmount(val: string): boolean {
	return !Number.isNaN(Number(val)) && Number(val) >= 0.01;
}
